/**
 * @file 좌측 사이드바 접기/펼치기 공용 껍데기 — 메일 폴더 · 채널 · 사내 게시판 · 결재 양식(관리자).
 *
 * <h3>왜 공용인가</h3>
 * <p>같은 동작을 네 화면에 각각 만들면 토글 위치·저장 키·애니메이션 길이·자동 접기 규칙이 곧
 * 갈라진다. 이 프로젝트는 이미 그 파편화를 겪었다(사이드바 하위 컴포넌트를 렌더 본문 안에서
 * 정의해 애니메이션이 죽은 건이 메일→채널로 그대로 번졌다). 규칙은 여기 한 곳에만 둔다.
 *
 * <h3>이 컴포넌트가 <b>책임지지 않는</b> 것</h3>
 * <p>배경색·테두리·패딩·헤더는 각 화면 소관이다. 여기서 손대면 네 화면의 시각적 정체성이
 * 하나로 뭉개진다. 이 컴포넌트가 강제하는 건 <b>폭 · 토글 · 저장 · overflow</b> 넷뿐이고,
 * 나머지는 {@code sx} 로 그대로 흘려보낸다.
 */
import React, {
    useCallback, useLayoutEffect, useMemo, useRef, useState,
} from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { ds } from "../../styles/designSystem";

const C = ds.colors;

/** 폭 애니메이션 길이. 자동 접기 예고(REVEAL_MS)와 짝이라 함께 본다. */
const WIDTH_MS = 220;
/** 자동 접기 전 "여기 사이드바가 있다"를 보여 주는 시간. 바로 접으면 존재 자체를 모른다. */
const REVEAL_MS = 900;

const readLS = (k) => {
    try { return localStorage.getItem(k); } catch { return null; }   // Safari 프라이빗 등
};
const writeLS = (k, v) => {
    try { localStorage.setItem(k, v); } catch { /* 저장 실패해도 동작은 유지된다 */ }
};

/**
 * @param {string}   storageKey    localStorage/sessionStorage 접두사. 화면마다 고유해야 한다.
 * @param {boolean}  [persist=true] 접힘 상태를 localStorage 에 남길지. false 면 <b>매 진입마다
 *   펼친 채로</b> 시작한다(자동 접기 예고는 그대로 돈다 — 그건 sessionStorage 라 세션 단위다).
 *   채널 화면이 «기억» 을 전부 걷어내면서 생긴 옵션이라 기본값은 기존 동작(저장함)이다.
 * @param {number}   width         펼침 폭(px)
 * @param {number}   [collapsedWidth=48]
 * @param {number}   [autoCollapseBelow=1200] 이 폭 미만에서만 자동 접기가 돈다
 * @param {string}   [expandTitle] 접힘 상태 토글 버튼 툴팁
 * @param {string}   [collapseTitle]
 * @param {Function} [renderCollapsed] ({ expand }) => 접힘 상태 48px 레일 내용
 * @param {Object}   [sx]          바깥 Box 에 그대로 합쳐진다(테두리·배경·높이 등)
 * @param {React.ReactNode|Function} children 펼침 내용. 함수면 ({ collapsed, expand, toggle }) 를 받는다
 */
export default function CollapsibleSidebar({
    storageKey,
    persist = true,
    width,
    collapsedWidth = 48,
    autoCollapseBelow = 1200,
    expandTitle = "사이드바 펼치기",
    collapseTitle = "사이드바 접기",
    renderCollapsed,
    sx,
    children,
}) {
    const shownKey = storageKey + ".shown";

    const [collapsed, setCollapsed] = useState(() => {
        const saved = persist ? readLS(storageKey) : null;
        if (saved !== null) return saved === "1";
        return false;                       // 첫 렌더는 항상 펼침 — 자동 접기는 아래 effect 가 맡는다
    });

    /**
     * 사용자가 <b>직접</b> 토글했을 때만 저장한다.
     * 자동 접기까지 저장하면 다음에 넓은 화면에서 열어도 접힌 채로 뜬다 — 좁아서 접었던 건데
     * 넓어져도 접혀 있으면 고장으로 읽힌다.
     */
    const toggle = useCallback(() => {
        setCollapsed((prev) => {
            const next = !prev;
            if (persist) writeLS(storageKey, next ? "1" : "0");
            return next;
        });
    }, [storageKey, persist]);

    /**
     * 접힘 상태에서 항목을 고른 경우의 펼침. <b>저장하지 않는다</b> — 직접 토글이 아니라
     * "고르려면 보여야 한다"는 보조 동작이라, 저장하면 접어 둔 사용자의 선택을 덮어쓴다.
     */
    const expand = useCallback(() => setCollapsed(false), []);

    /* ── 자동 접기 — "보여준 다음 접기" ──────────────────────────────
     * 0ms 펼침 렌더 → 900ms 접기 시작 → +220ms 완료.
     * 접지 않는 경우 셋: (1) 애니메이션 전 사용자 입력, (2) 저장값 존재, (3) 뷰포트 ≥ 1200px.
     * ------------------------------------------------------------ */

    /**
     * 이 마운트가 «예고»를 맡았는지. StrictMode 대응의 핵심이다.
     *
     * <p>StrictMode 는 effect 를 mount→cleanup→mount 로 두 번 돌린다. 첫 실행이 sessionStorage
     * 플래그를 이미 찍어 두므로, 두 번째 실행은 그걸 보고 "이번 세션에 이미 보여 줬다"고 <b>오판</b>해
     * 예고 없이 즉시 접어 버린다. 플래그를 <i>누가</i> 찍었는지를 ref 로 구분해야 한다.
     * ref 는 StrictMode 이중 실행 사이에 유지되고, 진짜 재진입에서는 새로 만들어진다.
     */
    const claimedReveal = useRef(false);

    useLayoutEffect(() => {
        if (persist && readLS(storageKey) !== null) return;      // (2) 저장값이 이기다
        if (window.innerWidth >= autoCollapseBelow) return;      // (3) 넓으면 접을 이유가 없다

        // 재진입은 애니메이션 없이 바로 접힘. useLayoutEffect 라 첫 페인트 전에 폭이 확정돼
        // transition 이 걸리지 않는다 — 이미 아는 사람에게 매번 900ms 를 보여 주면 느리다.
        let alreadyShown = false;
        try { alreadyShown = sessionStorage.getItem(shownKey) !== null; } catch { /* 무시 */ }
        if (alreadyShown && !claimedReveal.current) { setCollapsed(true); return; }

        claimedReveal.current = true;
        try { sessionStorage.setItem(shownKey, "1"); } catch { /* 무시 */ }

        const timer = setTimeout(() => setCollapsed(true), REVEAL_MS);
        // (1) 폴더를 고르려는 순간 접히면 손이 헛나간다. capture 로 받아 도중에 멈추는 핸들러가
        //     있어도 놓치지 않는다. 토글 버튼 클릭도 pointerdown 이 먼저라 여기서 함께 취소된다.
        const cancel = () => clearTimeout(timer);
        const opts = { capture: true };
        window.addEventListener("pointerdown", cancel, opts);
        window.addEventListener("keydown", cancel, opts);

        return () => {
            clearTimeout(timer);
            window.removeEventListener("pointerdown", cancel, opts);
            window.removeEventListener("keydown", cancel, opts);
        };
        // storageKey 가 런타임에 바뀌는 화면은 없다. 마운트 1회로 고정한다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ctx = useMemo(() => ({ collapsed, expand, toggle }), [collapsed, expand, toggle]);

    return (
        <Box
            sx={{
                height: "100%",
                ...sx,
                // 아래 넷은 이 컴포넌트의 책임이라 sx 로 덮이지 않게 뒤에 둔다.
                width: collapsed ? collapsedWidth : width,
                flexShrink: 0,                       // 없으면 좁은 화면에서 눌려 내용이 깨진다
                overflow: "hidden",                  // 48px 밖으로 펼침 내용이 새어 본문을 덮으면 안 된다
                transition: `width ${WIDTH_MS}ms ease`,   // 폭에만 — 내용에 걸면 글자가 찌그러진다
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* 토글 — 상단 우측 고정. 접히면 같은 자리에 방향만 뒤집힌 아이콘이 남아
                "펼칠 수 있다"는 단서가 끊기지 않는다. */}
            <Box sx={{
                flexShrink: 0, display: "flex", alignItems: "center", minHeight: 32,
                justifyContent: collapsed ? "center" : "flex-end",
                px: collapsed ? 0 : 0.6, pt: 0.6,
            }}>
                <Tooltip title={collapsed ? expandTitle : collapseTitle} placement="right">
                    <IconButton
                        size="small"
                        onClick={toggle}
                        aria-expanded={!collapsed}
                        aria-label={collapsed ? expandTitle : collapseTitle}
                        sx={{ p: 0.3, color: C.textMuted, "&:hover": { color: C.primary } }}
                    >
                        {/* 메인 사이드바 토글과 같은 화살표를 쓴다. 아이콘 하나를 회전시켜
                            «지금 어느 방향으로 열고 닫히는지» 를 그대로 보여 준다. */}
                        <ChevronLeftRoundedIcon sx={{
                            fontSize: 18, transition: "transform 160ms ease",
                            transform: collapsed ? "rotate(180deg)" : "none",
                        }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* 내용 — 펼침/접힘 각각 <b>고정 폭</b>이다. 폭을 100% 로 두면 컨테이너가 줄어드는
                220ms 동안 내부가 같이 리플로우돼 글자가 찌그러진다. 고정해 두고 바깥에서 잘라낸다. */}
            {collapsed ? (
                <Box
                    // 접힘 레일 안의 어떤 항목을 눌러도 펼친다 — 네 화면이 각자 기억하지 않아도
                    // "접었더니 못 고르겠다"가 생기지 않게 여기서 한 번에 보장한다.
                    onClick={expand}
                    sx={{
                        width: collapsedWidth, minWidth: collapsedWidth,
                        flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
                    }}
                >
                    {renderCollapsed ? renderCollapsed({ expand }) : null}
                </Box>
            ) : (
                <Box sx={{
                    width, minWidth: width,
                    flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
                }}>
                    {typeof children === "function" ? children(ctx) : children}
                </Box>
            )}
        </Box>
    );
}
